<template>
  <div v-if="user" class="min-h-screen bg-gray-100">
    <SidebarMenu :user="user" :isMenuOpen="isMenuOpen" @toggle-menu="toggleMenu" @close-menu="closeMenu"
      @logout="logout" />

    <div class="md:ml-64">
      <main class="p-4">
        <div class="p-4">
          <h1 class="text-xl font-bold">Prospectos / Historial</h1>
          <p class="text-gray-600 mb-4">Matrícula: {{ studentId || 'N/A' }}</p>

          <!-- Nueva leyenda responsiva -->
          <div class="mb-6 bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded">
            <p class="text-md font-semibold mb-2 text-center">
              Importante: Recuerda dar seguimiento a tus prospectos recomendados
            </p>
            <div class="flex flex-wrap justify-center items-center space-x-2">
              <div class="flex items-center space-x-1">
                <span class="w-3 h-3 bg-green-500 inline-block"></span>
                <span class="text-xs">5 días</span>
              </div>
              <div class="flex items-center space-x-1">
                <span class="w-3 h-3 bg-blue-900 inline-block"></span>
                <span class="text-xs">4 días</span>
              </div>
              <div class="flex items-center space-x-1">
                <span class="w-3 h-3 bg-blue-300 inline-block"></span>
                <span class="text-xs">3 días</span>
              </div>
              <div class="flex items-center space-x-1">
                <span class="w-3 h-3 bg-yellow-400 inline-block"></span>
                <span class="text-xs">2 días</span>
              </div>
              <div class="flex items-center space-x-1">
                <span class="w-3 h-3 bg-yellow-500 inline-block"></span>
                <span class="text-xs">1 días</span>
              </div>
              <div class="flex items-center space-x-1">
                <span class="w-3 h-3 bg-red-400 inline-block"></span>
                <span class="text-xs">Vence hoy</span>
              </div>
            </div>
          </div>

          <!-- Rest of the existing component remains the same -->
          <div class="hidden mb-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <!-- Filter inputs remain unchanged -->
          </div>

          <div v-if="loading" class="flex justify-center my-8">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>

          <div v-else-if="error" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {{ error }}
          </div>

          <div v-else-if="prospects && prospects.length > 0" class="mb-6">
            <h2 class="text-lg font-semibold mb-2">Prospectos Registrados:</h2>

            <!-- Vista móvil: tarjetas individuales -->
            <div class="grid grid-cols-1 gap-4 sm:hidden">
              <div v-for="prospect in prospects" :key="prospect.ProspectId" :class="[
                'bg-white shadow overflow-hidden rounded-md p-4 border-l-4',
                getExpirationBackgroundColor(prospect.ExpirationId, prospect.ExpirationDate)
              ]">
                <div class="grid grid-cols-2 gap-y-2">
                  <div class="text-sm font-medium text-gray-500">Nombre:</div>
                  <div class="text-sm uppercase">{{ prospect.Names }} {{ prospect.FatherLastName }} {{
                    prospect.MotherLastName }}</div>

                  <div class="text-sm font-medium text-gray-500">Celular:</div>
                  <div class="text-sm">{{ prospect.Mobile || 'N/A' }}</div>

                  <div class="text-sm font-medium text-gray-500 col-span-2">Intereses</div>
                  <div class="text-sm font-medium text-gray-500">Periodo:</div>
                  <div class="text-sm">{{ prospect.PeriodId || 'N/A' }}</div>
                  <div class="text-sm font-medium text-gray-500">Campus:</div>
                  <div class="text-sm">{{ prospect.Campus || 'N/A' }}</div>
                  <div class="text-sm font-medium text-gray-500">Carrera:</div>
                  <div class="text-sm">{{ prospect.Career || 'N/A' }}</div>
                  <div class="text-sm font-medium text-gray-500">Turno:</div>
                  <div class="text-sm">{{ prospect.ShiftId || 'N/A' }}</div>

                  <div class="text-sm font-medium text-gray-500">Estatus:</div>
                  <div class="text-sm">
                    {{ combineStatusAndExpiration(prospect.Status, prospect.ExpirationDate) }}
                  </div>
                  <div class="col-span-2 mt-4 flex flex-col space-y-2">
                    <button @click="addFollowUp(prospect.ProspectId)"
                      class="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                      <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                      </svg>
                      Agregar seguimiento
                    </button>
                    <button @click="viewFollowUps(prospect.ProspectId)"
                    class="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                      <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z">
                        </path>
                      </svg>
                      Ver seguimientos
                    </button>
                    <button @click="viewDetails(prospect.ProspectId)"
                    class="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                      <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      Ver detalles
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Vista escritorio: tabla tradicional -->
            <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg hidden sm:block">
              <table class="min-w-full divide-y divide-gray-300">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="px-3 py-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500">Nombre
                    </th>
                    <!-- Celular: oculto en pantallas medianas (md) -->
                    <th class="px-3 py-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500 hidden lg:table-cell">Celular
                    </th>
                    <!-- Intereses: oculto en pantallas medianas y pequeñas -->
                    <th class="px-3 py-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500 hidden xl:table-cell">
                      Intereses</th>
                    <th class="px-3 py-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500">Estatus
                    </th>
                    <th class="px-3 py-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500">Opciones
                    </th>
                  </tr>
                </thead>
                <tbody
                  class="divide-y divide-gray-200 bg-white whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                  <tr v-for="prospect in prospects" :key="prospect.ProspectId"
                    :class="getExpirationBackgroundColor(prospect.ExpirationId, prospect.ExpirationDate)">
                    <td class="whitespace-nowrap px-3 py-4 text-center uppercase">
                      {{ prospect.Names }} {{ prospect.FatherLastName }} {{ prospect.MotherLastName }}
                    </td>
                    <!-- Celular: oculto en pantallas medianas (md) -->
                    <td class="whitespace-nowrap px-3 py-4 text-center hidden lg:table-cell">
                      {{ prospect.Mobile || 'N/A' }}
                    </td>
                    <!-- Intereses: oculto en pantallas medianas y pequeñas -->
                    <td class="whitespace-nowrap px-3 py-4 text-center uppercase hidden xl:table-cell">
                      <span class="block">Periodo {{ prospect.PeriodId || 'N/A' }}</span>
                      <span class="block">Campus {{ prospect.Campus || 'N/A' }}</span>
                      <span class="block">Carrera {{ prospect.Career || 'N/A' }}</span>
                      <span class="block">Turno {{ prospect.ShiftId || 'N/A' }}</span>
                    </td>
                    <td class="whitespace-nowrap px-3 py-4 text-center">
                      {{ combineStatusAndExpiration(prospect.Status, prospect.ExpirationDate) }}
                    </td>
                    <td class="whitespace-nowrap px-3 py-4 text-center">
                      <div class="buttons-container">
                        <!-- Botón Agregar seguimiento -->
                        <div class="tooltip-wrapper">
                          <button @click="addFollowUp(prospect.ProspectId)"
                            class="inline-flex items-center justify-center p-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                            </svg>
                          </button>
                          <div class="tooltip tooltip-add">
                            <span class="tooltip-text">Agregar seguimiento</span>
                            <svg class="tooltip-arrow" width="16" height="10" viewBox="0 0 16 10">
                              <path d="M8 0L0 10H16L8 0Z" fill="currentColor" />
                            </svg>
                          </div>
                        </div>

                        <!-- Botón Ver seguimientos -->
                        <div class="tooltip-wrapper">
                          <button @click="viewFollowUps(prospect.ProspectId)"
                            class="inline-flex items-center justify-center p-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z">
                              </path>
                            </svg>
                          </button>
                          <div class="tooltip tooltip-view">
                            <span class="tooltip-text">Ver seguimientos</span>
                            <svg class="tooltip-arrow" width="16" height="10" viewBox="0 0 16 10">
                              <path d="M8 0L0 10H16L8 0Z" fill="currentColor" />
                            </svg>
                          </div>
                        </div>

                        <!-- Botón Ver detalles -->
                        <div class="tooltip-wrapper">
                          <button @click="viewDetails(prospect.ProspectId)"
                            class="inline-flex items-center justify-center p-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                            </svg>
                          </button>
                          <div class="tooltip tooltip-details">
                            <span class="tooltip-text">Ver detalles</span>
                            <svg class="tooltip-arrow" width="16" height="10" viewBox="0 0 16 10">
                              <path d="M8 0L0 10H16L8 0Z" fill="currentColor" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination remains unchanged -->
          </div>

          <div v-else class="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded mb-4">
            No hay prospectos disponibles para mostrar.
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import SidebarMenu from '@/components/SidebarMenu.vue';
import { getProspects } from "@/services/api";

export default {
  components: {
    SidebarMenu
  },
  data() {
    return {
      user: null,
      isMenuOpen: false,
      prospects: null,
      loading: true,
      error: null,
      page: 1,
      limit: 50,
      totalRecords: 0,
      filters: {
        name: '',
        statusId: '',
        startDate: '1900-12-12',
        endDate: '2090-12-12'
      },
      studentId: null,
    };
  },
  async created() {
    await this.getUserFromBackend();
  },
  methods: {
    addFollowUp(prospectId) {
      this.$router.push(`/agregar-seguimiento/${prospectId}`);
    },
    viewDetails(prospectId) {
      this.$router.push(`/detalle-prospecto/${prospectId}`);
    },
    viewFollowUps(prospectId) {
      this.$router.push(`/historial-seguimiento/${prospectId}`);
    },
    // Método para formatear fechas en formato yyyy-mm-dd
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toISOString().split('T')[0]; // Formato YYYY-MM-DD
    },


    async getUserFromBackend() {
      try {
        const response = await axios.get('/me');
        this.user = response.data.user;

        // Extraer el ID de estudiante
        if (this.user.email && this.user.email.match(/^a(\d+)@/)) {
          this.studentId = this.user.email.match(/^a(\d+)@/)[1];

          await this.loadData();
        } else {
          this.error = "No se pudo extraer el ID de estudiante del email.";
          this.loading = false;
        }
      } catch (error) {
        console.error("Error obteniendo usuario", error);
        this.$router.push('/login');
      }
    },

    async loadData() {
      try {
        this.loading = true;

        // Construir el filtro según la documentación
        const filter = JSON.stringify([
          { property: "pSource", value: "GET_Prospects" },
          { property: "pName", value: this.filters.name },
          { property: "pStatusId", value: this.filters.statusId },
          { property: "pPromotorName", value: "" },
          { property: "pStartDate", value: this.filters.startDate },
          { property: "pEndDate", value: this.filters.endDate },
          { property: "pUserName", value: this.studentId || "" }
        ]);

        const response = await getProspects({
          _dc: new Date().getTime(),
          page: this.page,
          start: (this.page - 1) * this.limit,
          limit: this.limit,
          filter: filter
        });

        this.prospects = response.data.prospects || [];
        this.totalRecords = response.data.totalCount || 0;
        this.loading = false;

      } catch (error) {
        console.error("Error al obtener los datos:", error);
        this.error = "Error al cargar los datos. Por favor, intente nuevamente.";
        this.loading = false;
      }
    },

    prevPage() {
      if (this.page > 1) {
        this.page--;
        this.loadData();
      }
    },

    nextPage() {
      if ((this.page * this.limit) < this.totalRecords) {
        this.page++;
        this.loadData();
      }
    },

    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
    },

    closeMenu() {
      this.isMenuOpen = false;
    },

    getExpirationColor(expirationId) {
      if (!expirationId) {
        return 'border-gray-300';
      }

      switch (parseInt(expirationId)) {
        case 2: return 'border-red-500';
        case 3: return 'border-yellow-500';
        case 4: return 'border-yellow-400';
        case 5: return 'border-blue-300';
        case 6: return 'border-blue-900';
        case 7: return 'border-green-500';
        default: return 'border-gray-300';
      }
    },

    getExpirationBackgroundColor(expirationId) {
      if (!expirationId) {
        return 'bg-white';
      }

      switch (parseInt(expirationId)) {
        case 2: return 'bg-red-400 border-red-500';
        case 3: return 'bg-yellow-500 border-yellow-500';
        case 4: return 'bg-yellow-100 border-yellow-400';
        case 5: return 'bg-yellow-50 border-blue-300';
        case 6: return 'bg-blue-900 bg-opacity-10 border-blue-900';
        case 7: return 'bg-green-100 border-green-500';
        default: return 'bg-white';
      }
    },

    // New method to combine status and expiration date
    combineStatusAndExpiration(status, expirationDate) {
      if (!expirationDate) {
        return status || 'Pendiente';
      }

      const combinedStatus = status ? `${status} ` : '';
      return `${combinedStatus} (${expirationDate})`;
    },

    async logout() {
      try {
        // Obtener nuevo token CSRF
        await axios.get('/sanctum/csrf-cookie');

        // Esperar un momento para asegurar que la cookie se haya establecido
        await new Promise(resolve => setTimeout(resolve, 100));

        // Realizar la petición de logout
        const response = await axios.post('/logout', {}, {
          withCredentials: true,
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });
        console.log(response);
        // Limpiar cookies locales
        document.cookie.split(";").forEach(function (c) {
          document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
        });
        // Redirigir al login
        this.$router.push('/login');
      } catch (error) {
        console.error('Error al cerrar sesión:', error.response?.data || error);
      }
    }
  }
};
</script>